import { useMemo, useState, type ReactElement } from 'react'
import { isEmpty, searchIncludes, type TeamData } from '@terros/common'
import { Modal, ScrollArea, Stack, Text, TextInput } from '@mantine/core'
import { Size, useTranslation } from '@front/model'
import { PrefetchedTeamInfo } from '@components'
import type { DashboardScopeTarget } from './types'
import { DashboardScopeOptionButton } from './DashboardScopeOptionButton'

type DashboardTeamPickerModalProps = {
  opened: boolean
  teams: TeamData[]
  onClose: () => void
  onSelect: (target: DashboardScopeTarget) => void
}

export const DashboardTeamPickerModal = ({
  opened,
  teams,
  onClose,
  onSelect,
}: DashboardTeamPickerModalProps): ReactElement => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const filteredTeams = useMemo(() => {
    const query = search.trim()
    if (isEmpty(query)) return teams
    return teams.filter((team) => searchIncludes([team.name], query))
  }, [search, teams])

  const handleClose = (): void => {
    setSearch('')
    onClose()
  }

  const handleSelect = (target: DashboardScopeTarget): void => {
    setSearch('')
    onSelect(target)
  }

  return (
    <Modal opened={opened} title={t('Teams')} onClose={handleClose}>
      <Stack gap='sm'>
        <TextInput
          value={search}
          placeholder={t('Search teams')}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
        <ScrollArea.Autosize mah={360}>
          <Stack gap={Size.gap.list}>
            {filteredTeams.map((team) => (
              <DashboardScopeOptionButton
                key={team.teamId}
                onClick={() => handleSelect({ entityId: team.teamId, name: team.name, kind: 'team' })}
              >
                <PrefetchedTeamInfo linked={false} team={team} size='sm' />
              </DashboardScopeOptionButton>
            ))}
            {isEmpty(filteredTeams) && <Text c='dimmed'>{t('No teams found')}</Text>}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </Modal>
  )
}
